/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DnsPolicyModule
 */

import {
    Component,
    Inject,
    OnInit,
} from '@angular/core';

import {
    IAviCollectionDataGridConfig,
    IAviCollectionDataGridRow,
} from 'ng/modules/data-grid/components/avi-collection-data-grid/avi-collection-data-grid.types';

import { L10nService } from '@vmw/ngx-vip';
import { DnsPolicyCollection } from 'ajs/modules/dns/factories/dns-policy.collection.factory';

import * as globalL10n from 'global-l10n';
import * as l10n from './dns-policy-list.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TDnsPolicyCollection = typeof DnsPolicyCollection;

/**
 * @description DNS Policy list page.
 *
 * @author Abhinesh Gour
 */
@Component({
    selector: 'dns-policy-list',
    templateUrl: './dns-policy-list.component.html',
})
export class DnsPolicyListComponent implements OnInit {
    /**
     * Global Keys from source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * DnsPolicy collection object.
     */
    public readonly dnsPolicyCollection: DnsPolicyCollection;

    /**
     * DnsPolicy grid config object.
     */
    public dnsPolicyGridConfig: IAviCollectionDataGridConfig;

    constructor(
        private l10nService: L10nService,
        @Inject(DnsPolicyCollection)
        DnsPolicyCollection: TDnsPolicyCollection,
    ) {
        l10nService.registerSourceBundles(dictionary);
        this.dnsPolicyCollection = new DnsPolicyCollection();
    }

    /** @override */
    public ngOnInit(): void {
        const { objectName } = this.dnsPolicyCollection;

        this.dnsPolicyGridConfig = {
            id: `${objectName}-list-page`,
            collection: this.dnsPolicyCollection,
            fields: [{
                id: 'name',
                label: this.l10nService.getMessage(globalL10nKeys.nameLabel),
                transform: (row: IAviCollectionDataGridRow) => row.getName(),
            },
            {
                id: 'total-dns-rules',
                label: this.l10nService.getMessage(l10nKeys.columnTitleTotalDnsRules),
                transform: (row: IAviCollectionDataGridRow) => {
                    const { rule: { config: ruleConfig } } = row.getConfig();

                    return ruleConfig ? ruleConfig.length : 0;
                },
            }],
            layout: {
                placeholderMessage: this.l10nService.getMessage(l10nKeys.gridPlaceholder),
            },
        };
    }

    /**
     * @override
     */
    public ngOnDestroy(): void {
        this.dnsPolicyCollection.destroy();
    }
}
